import "@/lib/chart";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Loader2, TriangleAlert, X } from "lucide-react";
import type { ReactNode } from "react";
import { authClient } from "@/auth/client";
import { ApiError } from "@/lib/api";
import { useSchedulingData } from "@/hooks/useSchedulingData";
import { useLaunchDates } from "@/hooks/useLaunchDates";
import { useLastUpdated } from "@/hooks/useLastUpdated";
import { useFilters } from "@/hooks/useFilters";
import { getAvailableGranularities, getPeriodFamily } from "@/lib/periods";
import { useScopeOptions } from "@/hooks/useScopeOptions";
import { useRollupScopeOptions } from "@/hooks/useRollupScopeOptions";
import { buildScopeCounts, resolveReverseFill } from "@/lib/scopeOptions";
import { nextDrillLevel } from "@/lib/drillScope";
import { useMetrics } from "@/hooks/useMetrics";
import { useTrendData } from "@/hooks/useTrendData";
import { useDrillData } from "@/hooks/useDrillData";
import { useRollupData } from "@/hooks/useRollupData";
import { useRollupComputed } from "@/hooks/useRollupComputed";
import { useRollupTrend } from "@/hooks/useRollupTrend";
import { getDataPath, needsRaw, needsRollup } from "@/lib/rollupFlag";
import { comparePaths } from "@/lib/rollupCompare";
import { NavRail } from "@/components/dashboard/NavRail";
import { FilterBar } from "@/components/dashboard/FilterBar";
import { ScopeBar } from "@/components/dashboard/ScopeBar";
import { PeriodBar } from "@/components/dashboard/PeriodBar";
import { UtilizationBreakdown } from "@/components/dashboard/UtilizationBreakdown";
import { AppointmentTypeBar } from "@/components/dashboard/AppointmentTypeBar";
import { UtilizationKPICards } from "@/components/dashboard/UtilizationKPICards";
import { CapacityCards } from "@/components/dashboard/CapacityCards";
import { UtilizationChart } from "@/components/dashboard/UtilizationChart";
import { CapacityChart } from "@/components/dashboard/CapacityChart";
import { DrillDownTable } from "@/components/dashboard/DrillDownTable";
import { GuideTab } from "@/components/dashboard/GuideTab";
import { RevenueTab } from "@/components/dashboard/RevenueTab";
import { CancellationTab } from "@/components/dashboard/CancellationTab";

// "(Beta)" travels with the label rather than being painted on the nav rail, so
// it reaches the rail and the filter bar's active-tab heading from one place --
// these two tabs have not been through UAT and should say so wherever they are
// named. The keys are unchanged, so URLs and stored state are unaffected.
const TABS = [
  { key: "utilization", label: "Capacity & Utilization" },
  { key: "revenue", label: "Revenue (Beta)" },
  // Hidden rather than deleted: the tab, its render branch, its rollup columns
  // and its verifier all stay where they are, so bringing it back is removing
  // this one flag. Everything else here is derived from it.
  { key: "cancellation", label: "Cancellation (Beta)", hidden: true },
  { key: "guide", label: "Guide" },
] as const;

const VISIBLE_TABS = TABS.filter((t) => !("hidden" in t && t.hidden));

// Drives the router's ?tab= validation as well as the nav rail, so a hidden
// tab is unreachable by URL too and not merely absent from the rail --
// ?tab=cancellation falls back to utilization the way any unknown value does.
// A saved link is the one way a tab nobody has UAT-signed could still be
// opened, which is the case hiding it is meant to prevent.
export const TAB_KEYS = VISIBLE_TABS.map((t) => t.key);

export type TabKey = (typeof TABS)[number]["key"];

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <h2 className="text-[11px] font-semibold uppercase tracking-[0.6px] text-gray-500 mb-2">
      {children}
    </h2>
  );
}

function PlaceholderTab({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 gap-3 rounded-lg border border-dashed border-gray-200 bg-gray-50/40">
      <div className="text-[11px] font-semibold uppercase tracking-[0.6px] text-gray-400">
        {label}
      </div>
      <div className="text-[16px] font-semibold text-gray-600">Coming soon</div>
      <div className="text-[13px] text-gray-400 max-w-sm text-center">
        This view is in development. Check back after the next release.
      </div>
    </div>
  );
}

function LoadingOverlay() {
  return (
    <div className="flex flex-col items-center justify-center py-32 gap-5" role="status">
      <div className="relative flex h-16 w-16 items-center justify-center">
        <span
          className="absolute inset-0 rounded-full animate-ping"
          style={{ background: "color-mix(in srgb, var(--brand-light-teal) 30%, transparent)" }}
        />
        <span
          className="absolute inset-0 rounded-full"
          style={{ background: "color-mix(in srgb, var(--brand-light-teal) 18%, transparent)" }}
        />
        <Loader2
          className="relative h-8 w-8 animate-spin"
          style={{ color: "var(--brand-dark-teal)" }}
        />
      </div>
      <p
        className="text-[15px] font-medium tracking-wide"
        style={{ color: "var(--brand-dark-teal)" }}
      >
        Loading…
      </p>
    </div>
  );
}

// Non-blocking: the dashboard still renders (with empty data) underneath, so
// layout/design work isn't blocked when the data source is unavailable.
//
// The message shown here is either a sanitized server message or one this
// codebase wrote about the request -- never raw exception text; api() and
// apiError.ts keep that distinction. The status code and log reference are what
// make a report actionable without publishing the internals.
function ErrorBanner({
  message,
  status,
  reference,
  onDismiss,
}: {
  message: string;
  status: number | null;
  reference?: string;
  onDismiss: () => void;
}) {
  return (
    <div
      className="mb-6 rounded-lg border px-4 py-3 flex items-start gap-3"
      style={{
        borderColor: "color-mix(in srgb, var(--brand-orange-dark) 35%, transparent)",
        background: "color-mix(in srgb, var(--brand-orange-light) 25%, white)",
      }}
    >
      <TriangleAlert
        className="h-4 w-4 shrink-0 mt-0.5"
        style={{ color: "var(--brand-orange-dark)" }}
      />
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-medium" style={{ color: "var(--brand-orange-dark)" }}>
          Failed to load data{status !== null ? ` — Error ${status}` : ""}
        </p>
        <p className="text-[12px] text-gray-600 mt-0.5 break-words">{message}</p>
        {reference && (
          <p className="text-[11px] text-gray-400 mt-1 font-mono">Reference {reference}</p>
        )}
      </div>
      <button
        onClick={onDismiss}
        aria-label="Dismiss error"
        className="shrink-0 rounded p-1 hover:bg-black/5 transition-colors"
      >
        <X className="h-3.5 w-3.5 text-gray-500" />
      </button>
    </div>
  );
}

export function AppPage() {
  const navigate = useNavigate();
  const { data: session } = authClient.useSession();
  const isAdmin = session?.user.role?.split(",").includes("admin") ?? false;
  // The tab is URL state, not component state, so Ctrl-R keeps you where you
  // were. `replace` because switching tabs is not a step worth stacking in
  // history -- Back should leave the dashboard, not walk the tabs you visited.
  const { tab: activeTab } = useSearch({ from: "/app" });
  const setActiveTab = (key: TabKey) =>
    navigate({ to: "/app", search: (prev) => ({ ...prev, tab: key }), replace: true });
  const [railCollapsed, setRailCollapsed] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(true);
  // Stores the dismissed message rather than a flag, so a *different* error
  // after a dismissal still surfaces — no effect needed to reset it.
  const [dismissedError, setDismissedError] = useState<string | null>(null);

  const {
    filters,
    setPeriod,
    setAsOfDate,
    setScope,
    setScopeRaw,
    resetScope,
    setAppointmentTypes,
    setDrillLevel,
    setCapacityView,
    setCapacityMetric,
    setUtilGranularity,
    setCapGranularity,
    setRevGranularity,
    setRevMetricGranularity,
    setExcludeZeroApptDays,
    setLaunchedBefore,
  } = useFilters();

  // The rollout schedule. Fetched once; the rollup path only needs the option
  // list, the raw path also needs the map to cut the same cohort client-side.
  const {
    options: launchDateOptions,
    firstLaunchDate,
    launchDates,
    eligibleSites,
    pendingCohort,
  } = useLaunchDates(filters.launchedBefore);

  // Only when the scope is narrowed to one site. Above site level there is no
  // single go-live to report, and a DVM reverse-fills its site, so this covers
  // the DVM case too.
  const siteLaunchDate = filters.scope.locationId
    ? (launchDates[filters.scope.locationId] ?? null)
    : null;

  const typeFilterActive = filters.appointmentTypes.length > 0;

  // Which groupings the selected period spans. Recomputed with the period so a
  // switch from T52W to MTD greys out Month rather than leaving a chart that
  // renders a single point.
  const availableGranularities = useMemo(
    () =>
      getAvailableGranularities(
        { startDate: filters.customStart, endDate: filters.customEnd },
        getPeriodFamily(filters.period)
      ),
    [filters.customStart, filters.customEnd, filters.period]
  );

  // Calendar periods drop their part-weeks when grouped by week, so those
  // charts cover fewer days than the KPI cards. Say so on the chart.
  const calendarPeriod = getPeriodFamily(filters.period) === "calendar";

  // Read before the raw fetch, because it decides whether that fetch happens.
  const dataPath = getDataPath();

  // needsRaw is false on the rollup path, so this issues no request at all and
  // `rows` stays empty. Every raw-path hook below still runs, over an empty array,
  // which costs nothing and keeps compare mode and ?data=raw working unchanged.
  const {
    rows,
    loading: rawLoading,
    fetching: rawFetching,
    error: rawError,
    // Held back until the rollout schedule has arrived, so a cutoff never renders
    // one frame of unfiltered rows under its own filter chip.
  } = useSchedulingData(filters, needsRaw(dataPath) && !pendingCohort, eligibleSites);

  // Reverse hierarchy: selecting any level auto-fills parents above it.
  // Uses setScopeRaw to set the full scope at once — avoids the forward cascade
  // in setScope which would clear siblings when countryCode is included in patch.
  function setScopeWithReverseFill(patch: Parameters<typeof setScope>[0]) {
    // Reads whichever row source this path has. resolveReverseFill is shared, so
    // the hierarchy is filled in identically either way; the rollup source is the
    // payload's unscoped doctor rows, which carry every identity column.
    const filled = resolveReverseFill(reverseFillRows, patch);
    if (filled) {
      setScopeRaw(filled);
      return;
    }
    setScope(patch);
  }

  // Clicking a drill row zooms in: it narrows the scope AND opens the next level
  // down, so the table shows what is inside what you clicked rather than
  // collapsing to the single row you already selected. At DVM there is no level
  // below, so it only narrows the scope -- and the peer list stays put, with
  // that doctor highlighted (see drillScope.ts).
  function drillIntoRow(patch: Parameters<typeof setScope>[0]) {
    setScopeWithReverseFill(patch);
    const next = nextDrillLevel(filters.drillLevel);
    if (next) setDrillLevel(next);
  }

  // Site and DVM counts for the header. nvaSiteCount is unscoped on purpose --
  // it sizes the benchmark, not the selection.
  const { scope: countScope } = filters;
  const rawCounts = useMemo(() => buildScopeCounts(rows, countScope), [rows, countScope]);
  const rawOptions = useScopeOptions(rows, filters.scope);

  const lastUpdated = useLastUpdated();

  const metrics = useMetrics(rows, filters);
  const trend = useTrendData(rows, filters);
  const { rows: drillRows, pinned: pinnedRows } = useDrillData(rows, filters);

  // ── Rollup path ───────────────────────────────────────────────────────────
  // ?data=rollup drives the UI from pre-aggregated rows; ?data=compare runs both
  // and logs any field that disagrees. The harnesses prove the builders on
  // combinations chosen in advance and cannot see a hook passing the wrong
  // granularity or dropping a filter -- this runs on whatever is actually
  // clicked. See src/lib/rollupFlag.ts.
  // The KPI cards, drill table and Capacity chart all read the payload fetched at
  // the Capacity chart's granularity.
  const rollupGranularity = filters.capGranularity;
  const {
    payload: rollupPayload,
    loading: rollupLoading,
    fetching: rollupFetching,
    error: rollupError,
  } = useRollupData(filters, rollupGranularity, needsRollup(dataPath));
  const rollupComputed = useRollupComputed(rollupPayload, filters, rollupGranularity);

  // A trend series can only be built from a payload fetched at its own
  // granularity (see useRollupTrend), and the two charts are grouped
  // independently -- so the Utilization chart gets its own fetch. When the two
  // granularities are equal, which is the default, React Query sees the same key
  // and this is the SAME request and cache entry, not a second one.
  const {
    payload: utilPayload,
    loading: utilLoading,
    fetching: utilFetching,
    error: utilError,
  } = useRollupData(filters, filters.utilGranularity, needsRollup(dataPath));

  // Both flags must span every path. Each hook already reports false when its own
  // fetch is switched off, so this needs no branch on dataPath -- and that is the
  // point: reading the RAW hook's flags alone meant the rollup path, which is the
  // default, had no loading state and no error banner at all. It rendered
  // instantly with an empty payload, so every figure showed 0 until the data
  // landed, and a failed fetch showed nothing whatsoever.
  const loading = rawLoading || rollupLoading || utilLoading;
  const error = rawError ?? rollupError ?? utilError;
  const rollupCapTrend = useRollupTrend(rollupPayload, filters, filters.capGranularity);
  const rollupUtilTrend = useRollupTrend(utilPayload, filters, filters.utilGranularity);
  const rollupScope = useRollupScopeOptions(rollupPayload, filters.scope);
  const useRollupScope = dataPath === "rollup" && rollupScope !== null;
  const options = useRollupScope ? rollupScope.options : rawOptions;
  // The row source the reverse-fill lookup reads. Raw rows on the raw path; the
  // payload's unscoped identity rows on the rollup path.
  const reverseFillRows = useRollupScope ? rollupScope.identity : rows;
  const { siteCount, dvmCount, nvaSiteCount } = useRollupScope ? rollupScope.counts : rawCounts;

  // Global flag thresholds — company-wide, unscoped, grouped by current drill level.
  // Computed directly from raw rows so a West Group filter doesn't shrink the reference set.
  //
  // Destructured OUTSIDE the memo, as in useMetrics and the other three hooks. The
  // dependency list was already narrow and correct, but destructuring inside left a
  // reference to `filters` that exhaustive-deps could only satisfy by depending on the
  // whole object -- which is exactly the change that would have made a display-only
  // toggle recompute this.
  const { drillLevel: thresholdLevel, customStart, customEnd } = filters;
  const rawThresholds = useMemo(() => {
    const level = thresholdLevel;
    const cyRows = rows.filter(
      (r) => r.schedule_date >= customStart && r.schedule_date <= customEnd
    );
    if (cyRows.length === 0) return { globalSeenThreshold: 0, globalAvailCeiling: 1 };

    const totals = new Map<string, { seen: number; worked: number; avail: number }>();
    for (const r of cyRows) {
      const key =
        level === "site"
          ? r.location_id
          : level === "dvm"
            ? r.resource_id
            : level === "division"
              ? r.division_name
              : level === "group"
                ? r.group_name
                : r.country_code;
      const e = totals.get(key) ?? { seen: 0, worked: 0, avail: 0 };
      e.seen += Number(r.completed_appointment_minutes) || 0;
      e.worked += Number(r.total_worked_minutes) || 0;
      e.avail += Number(r.available_minutes) || 0;
      totals.set(key, e);
    }

    // Over WORKED minutes, with available read from its column -- a transcription
    // of fetchThresholds, which is the rollup path's copy of this. The two are
    // compared field-by-field in compare mode, and that is how this one was
    // caught still ranking against the roster after the SQL had moved on:
    // verifyThresholds checks the SQL against a SQL oracle, so it cannot see a
    // client-side formula that has drifted.
    const seenRates = Array.from(totals.values())
      .map((e) => (e.worked > 0 ? e.seen / e.worked : 0))
      .sort((a, b) => a - b);
    const availRates = Array.from(totals.values())
      .map((e) => (e.worked > 0 ? e.avail / e.worked : 0))
      .sort((a, b) => a - b);

    // Use the value at the last bottom-20% index so <= catches all tied rows
    const p20idx = Math.max(0, Math.floor(seenRates.length * 0.2) - 1);
    const p80idx = Math.min(availRates.length - 1, Math.floor(availRates.length * 0.8));
    return {
      globalSeenThreshold: seenRates[p20idx] ?? 0,
      globalAvailCeiling: availRates[p80idx] ?? 1,
    };
  }, [rows, thresholdLevel, customStart, customEnd]);

  // On the rollup path the same two numbers arrive from SQL. They are the whole
  // reason the raw path fetches UNSCOPED rows: the cut-offs are company-wide by
  // definition, so a West Group view still ranks against all of NVA, and at DVM
  // level that meant an unscoped row per doctor in every browser to produce two
  // floats. Falling back to the raw memo keeps ?data=raw and compare mode working;
  // once the default flips, rawThresholds has no reader.
  const { globalSeenThreshold, globalAvailCeiling } =
    dataPath === "rollup" && rollupPayload
      ? {
          globalSeenThreshold: rollupPayload.thresholds.seen,
          globalAvailCeiling: rollupPayload.thresholds.avail,
        }
      : rawThresholds;

  useEffect(() => {
    // Both queries must be SETTLED, not merely non-empty. This used to check the
    // RAW query's flag only -- and raw rows cover a wide window that is filtered in
    // the browser, so changing period updates raw instantly while the rollup makes a
    // round trip. The comparator could therefore diff a fresh raw result against the
    // previous window's rollup and report a mismatch that was a timing gap rather
    // than a disagreement. isFetching rather than isPending, because a background
    // refetch still has stale data to show and would otherwise slip through.
    if (
      dataPath !== "compare" ||
      !rollupComputed ||
      loading ||
      rawFetching ||
      rollupFetching ||
      utilFetching
    ) {
      return;
    }
    // Scope belongs in the tag. comparePaths logs a match once per distinct
    // label, so without it every scoped run reused the unscoped label and went
    // silent -- and pinned ancestor rows only EXIST under a scope, so the one
    // thing that needed watching was the one thing never reported. A mismatch
    // would still have surfaced (its key carries the field paths), but "no new
    // line" is not evidence of a check having run.
    const sc = filters.scope;
    const scopeTag =
      [sc.countryCode, sc.groupName, sc.divisionName, sc.locationId, sc.resourceId].filter(Boolean)
        .length === 0
        ? "all"
        : [sc.countryCode, sc.groupName, sc.divisionName, sc.locationId, sc.resourceId]
            .map((v) => v ?? "*")
            .join(">");
    // The two chart granularities belong in the tag for the same reason scope
    // does: comparePaths logs a match once per label, so grouping the Utilization
    // chart differently reused the old label and went silent -- and that mixed
    // case is the only one where the two charts need two payloads, so it was the
    // one case that most needed watching.
    const tag =
      `${filters.period}/u:${filters.utilGranularity}/c:${filters.capGranularity}` +
      `/${filters.drillLevel}/${scopeTag}`;
    comparePaths(`${tag} kpi.current`, metrics.current, rollupComputed.metrics.current);
    comparePaths(`${tag} kpi.priorYear`, metrics.priorYear, rollupComputed.metrics.priorYear);
    comparePaths(`${tag} kpi.nvaAvg`, metrics.nvaAvg, rollupComputed.metrics.nvaAvg);
    // Both series. Only cap had ever been compared, which is why the Utilization
    // chart having no rollup source at all went unnoticed.
    // Both series. Only cap had ever been compared, which is why the Utilization
    // chart having no rollup source at all went unnoticed.
    if (rollupCapTrend) comparePaths(`${tag} trend.cap`, trend.cap.current, rollupCapTrend);
    if (rollupUtilTrend) comparePaths(`${tag} trend.util`, trend.util.current, rollupUtilTrend);
    // Drill rows are compared by id, since the two paths need not agree on order.
    const byId = new Map(rollupComputed.drill.rows.map((r) => [r.id, r]));
    comparePaths(
      `${tag} drill`,
      drillRows.map((r) => ({ id: r.id, siteCount: r.siteCount, metrics: r.metrics })),
      drillRows.map((r) => {
        const g = byId.get(r.id);
        return g ? { id: g.id, siteCount: g.siteCount, metrics: g.metrics } : null;
      })
    );
    // Pinned rows are positional, not keyed: both paths walk DRILL_LEVELS in the
    // same order, so a length difference here means the two disagree about WHICH
    // ancestors to show, which is worth reporting on its own.
    comparePaths(`${tag} pinned`, pinnedRows, rollupComputed.drill.pinned);
    // The dropdowns are navigation, not numbers: a level offering a different
    // set on one path than the other is a bug nobody would think to look for.
    if (rollupScope) {
      comparePaths(`${tag} scopeOptions`, rawOptions, rollupScope.options);
      comparePaths(`${tag} scopeCounts`, rawCounts, rollupScope.counts);
    }
    // Thresholds are the one figure computed on the SERVER for the rollup path
    // and in the browser for the raw one, so this compares two different
    // implementations rather than two callers of the same function.
    if (rollupPayload) {
      comparePaths(
        `${tag} thresholds`,
        { seen: rawThresholds.globalSeenThreshold, avail: rawThresholds.globalAvailCeiling },
        { seen: rollupPayload.thresholds.seen, avail: rollupPayload.thresholds.avail }
      );
    }
  }, [
    dataPath,
    rollupComputed,
    metrics,
    trend,
    drillRows,
    pinnedRows,
    rawThresholds,
    rawOptions,
    rawCounts,
    rollupScope,
    rollupPayload,
    rollupCapTrend,
    rollupUtilTrend,
    utilFetching,
    loading,
    rawFetching,
    rollupFetching,
    filters,
    rollupGranularity,
  ]);

  const handleSignOut = async () => {
    await authClient.signOut();
    navigate({ to: "/login" });
  };

  // ── Which path the UI actually renders ────────────────────────────────────
  // Everything above computes BOTH paths when compare mode is on. This is where
  // one of them is chosen. The rollup results existed and were verified for a
  // while before anything consumed them: rollupComputed was read only by the
  // comparator, so ?data=rollup fetched a payload and then drew the raw rows
  // anyway. These five bindings are the actual connection.
  const showRollup = dataPath === "rollup" && rollupComputed !== null;
  const shownMetrics = showRollup ? rollupComputed.metrics : metrics;
  const shownDrillRows = showRollup ? rollupComputed.drill.rows : drillRows;
  const shownPinned = showRollup ? rollupComputed.drill.pinned : pinnedRows;
  // Each chart's series comes from the payload fetched at ITS granularity.
  const shownUtilTrend = showRollup && rollupUtilTrend ? { current: rollupUtilTrend } : trend.util;
  const shownCapTrend = showRollup && rollupCapTrend ? { current: rollupCapTrend } : trend.cap;

  const activeTabLabel = TABS.find((t) => t.key === activeTab)!.label;
  // One period drives every data-backed tab — Revenue and Cancellation no
  // longer carry a second period state of their own.
  const usesPeriod = activeTab !== "guide";

  const periodState = {
    period: filters.period,
    asOfDate: filters.asOfDate,
    customStart: filters.customStart,
    customEnd: filters.customEnd,
  };
  const periodStateForBar = usesPeriod ? periodState : null;

  const scopeSlot = (
    <ScopeBar
      filters={filters}
      options={options}
      setScope={setScopeWithReverseFill}
      resetScope={resetScope}
      setAppointmentTypes={setAppointmentTypes}
      setExcludeZeroApptDays={setExcludeZeroApptDays}
    />
  );

  const periodSlot = usesPeriod ? (
    <PeriodBar
      periodState={periodState}
      setPeriod={setPeriod}
      setAsOfDate={setAsOfDate}
      launchedBefore={filters.launchedBefore}
      setLaunchedBefore={setLaunchedBefore}
      launchDateOptions={launchDateOptions}
      firstLaunchDate={firstLaunchDate}
      siteLaunchDate={siteLaunchDate}
    />
  ) : null;

  return (
    <div className="flex min-h-screen bg-background font-brand">
      <NavRail
        tabs={VISIBLE_TABS}
        activeTab={activeTab}
        onTabChange={(key) => setActiveTab(key as TabKey)}
        collapsed={railCollapsed}
        onToggleCollapse={() => setRailCollapsed((c) => !c)}
        userEmail={session?.user.email}
        isAdmin={isAdmin}
        onSignOut={handleSignOut}
        lastUpdated={lastUpdated?.toFormat("MMM d, yyyy")}
      />

      <div className="flex-1 min-w-0 flex flex-col">
        <FilterBar
          activeTabLabel={activeTabLabel}
          filters={filters}
          options={options}
          periodState={periodStateForBar}
          siteCount={siteCount}
          dvmCount={dvmCount}
          // A failed fetch leaves the counts unknown, not zero -- the same reason
          // they are dashed while loading.
          countsPending={loading || error !== null}
          filtersOpen={filtersOpen}
          onToggleFilters={() => setFiltersOpen((o) => !o)}
          setScope={setScopeWithReverseFill}
          setAppointmentTypes={setAppointmentTypes}
          setLaunchedBefore={setLaunchedBefore}
          scopeSlot={scopeSlot}
          periodSlot={periodSlot}
        />

        <main
          className="flex-1 px-8 pb-8"
          style={{ background: "color-mix(in srgb, var(--brand-cream) 35%, white)" }}
        >
          <div className="max-w-[1200px] mx-auto w-full pt-6">
            {error && error.message !== dismissedError && (
              <ErrorBanner
                message={error.message}
                status={error instanceof ApiError ? error.status : null}
                reference={error instanceof ApiError ? error.ref : undefined}
                onDismiss={() => setDismissedError(error.message)}
              />
            )}

            {/* Tab content — Guide is documentation and reads no data, so it renders
                ahead of the loading guard: it must stay usable exactly when the data
                layer is slow or down. */}
            {activeTab === "guide" ? (
              <GuideTab />
            ) : loading ? (
              <LoadingOverlay />
            ) : activeTab === "revenue" ? (
              <RevenueTab
                rollupPayload={rollupPayload}
                dataPath={dataPath}
                drillLevel={filters.drillLevel}
                setDrillLevel={setDrillLevel}
                rows={rows}
                filters={filters}
                startDate={filters.customStart}
                endDate={filters.customEnd}
                availableGranularities={availableGranularities}
                calendarPeriod={calendarPeriod}
                setRevGranularity={setRevGranularity}
                setRevMetricGranularity={setRevMetricGranularity}
                onRowClick={drillIntoRow}
              />
            ) : activeTab === "cancellation" ? (
              <CancellationTab
                rollupPayload={rollupPayload}
                dataPath={dataPath}
                drillLevel={filters.drillLevel}
                setDrillLevel={setDrillLevel}
                rows={rows}
                filters={filters}
                startDate={filters.customStart}
                endDate={filters.customEnd}
              />
            ) : activeTab !== "utilization" ? (
              <PlaceholderTab label={TABS.find((t) => t.key === activeTab)!.label} />
            ) : (
              <>
                <section className="mb-8">
                  <SectionLabel>
                    Utilization breakdown — current vs. prior year vs. NVA Avg. (Eligible Sites)
                  </SectionLabel>
                  <UtilizationBreakdown
                    current={shownMetrics.current}
                    priorYear={shownMetrics.priorYear}
                    nvaAvg={shownMetrics.nvaAvg}
                    typeFilterActive={typeFilterActive}
                  />
                </section>

                <section className="mb-8">
                  <SectionLabel>Utilization per DVM per day</SectionLabel>
                  <UtilizationKPICards
                    current={shownMetrics.current}
                    priorYear={shownMetrics.priorYear}
                    nvaAvg={shownMetrics.nvaAvg}
                    typeFilterActive={typeFilterActive}
                  />
                </section>

                <section className="mb-8">
                  <div className="flex items-center justify-between mb-2">
                    <SectionLabel>Scheduled Capacity</SectionLabel>
                    <div className="flex">
                      {(["avg", "total"] as const).map((v, i) => (
                        <button
                          key={v}
                          onClick={() => setCapacityView(v)}
                          className={[
                            "text-[12px] px-3 py-1 border border-black/15 transition-colors",
                            i === 0 ? "rounded-l-md" : "rounded-r-md -ml-px",
                            filters.capacityView === v
                              ? "bg-[var(--brand-dark-teal)] text-white border-[var(--brand-dark-teal)] z-10 relative"
                              : "bg-white text-gray-600 hover:bg-gray-50",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                        >
                          {v === "avg" ? "Average" : "Total"}
                        </button>
                      ))}
                    </div>
                  </div>
                  <CapacityCards
                    current={shownMetrics.current}
                    priorYear={shownMetrics.priorYear}
                    nvaAvg={shownMetrics.nvaAvg}
                    filters={filters}
                    siteCount={siteCount}
                    nvaSiteCount={nvaSiteCount}
                  />
                </section>

                <section className="mb-8">
                  <SectionLabel>Trends</SectionLabel>
                  <div className="grid grid-cols-2 gap-4 mb-7">
                    <UtilizationChart
                      trend={shownUtilTrend}
                      granularity={filters.utilGranularity}
                      availableGranularities={availableGranularities}
                      partialWeeksShown={calendarPeriod && filters.utilGranularity === "week"}
                      setUtilGranularity={setUtilGranularity}
                      typeFilterActive={typeFilterActive}
                    />
                    <CapacityChart
                      trend={shownCapTrend}
                      granularity={filters.capGranularity}
                      capacityMetric={filters.capacityMetric}
                      setCapacityMetric={setCapacityMetric}
                      availableGranularities={availableGranularities}
                      partialWeeksShown={calendarPeriod && filters.capGranularity === "week"}
                      partialMonthEdge={calendarPeriod && filters.capGranularity === "month"}
                      setCapGranularity={setCapGranularity}
                    />
                  </div>
                </section>

                <section className="mb-8">
                  <SectionLabel>Pet seen time — by appointment type</SectionLabel>
                  <AppointmentTypeBar
                    current={shownMetrics.current}
                    typeFilterCount={filters.appointmentTypes.length}
                  />
                </section>

                <DrillDownTable
                  rows={shownDrillRows}
                  pinned={shownPinned}
                  filters={filters}
                  setDrillLevel={setDrillLevel}
                  onRowClick={drillIntoRow}
                  globalSeenThreshold={globalSeenThreshold}
                  globalAvailCeiling={globalAvailCeiling}
                />
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
